/**
 * Patch Applier - Applies accessibility patches to the DOM
 *
 * DESIGN PRINCIPLES (based on overlay criticism research):
 * 1. No blanket ARIA injection - only apply specific, audited patches
 * 2. No style overrides that break existing AT compatibility
 * 3. Every patch is individually reversible
 * 4. Patches are transparent and auditable (data-helpbob attributes)
 * 5. Never interfere with existing ARIA attributes unless explicitly fixing them
 * 6. No keyboard trap creation - patches must not break tab order
 */

import type { PublicPatch, PatchAction } from '@helpbob/shared';
import { querySelectorAllDeep } from './shadow-query';
import { resolveTarget } from '@helpbob/shared/dist/target-note';
import {
  DANGEROUS_HTML_PATTERNS as SHARED_DANGEROUS_HTML_PATTERNS,
  disallowedHtmlNode,
  META_REFRESH_CONTENT_ATTRIBUTE,
  metaRefreshContentError,
  isMetaRefreshElement,
  isPageTitleInsert,
  skipLinkInsertParts,
  SKIP_LINK_CLASS,
  SKIP_LINK_STYLE,
  motionPauseInsertParts,
  MOTION_PAUSE_CLASS,
  MOTION_PAUSE_STYLE,
  MOTION_PAUSED_ATTRIBUTE,
  MOTION_CONTROLLED_ATTRIBUTE,
  MOTION_PAUSE_BLOCKED_ANCESTORS,
  isViewportFontClamp,
} from '@helpbob/shared/dist/html-safety';

/** Patterns that indicate potentially malicious HTML content — ONE list with
 *  the server's sanitizer and jsdom verifier (@helpbob/shared html-safety.ts,
 *  scanner#368). */
const DANGEROUS_HTML_PATTERNS = SHARED_DANGEROUS_HTML_PATTERNS;

/**
 * Client-side defense-in-depth HTML sanitization.
 * Mirrors the server-side checks in sanitize.ts.
 * Returns true if the HTML is safe, false if dangerous patterns are detected.
 */
/** #351 H1 — attributes a replaceTag may never add (see the replaceTag case). */
function isForbiddenAddedAttribute(name: string): boolean {
  return /^on/i.test(name) || /^(style|srcdoc)$/i.test(name);
}

/** #354 — an element with this data-helpbob-key key is already in the page. */
function hasDedupeKey(key: string): boolean {
  return Array.from(document.querySelectorAll('[data-helpbob-key]'))
    .some((e) => e.getAttribute('data-helpbob-key') === key);
}

function isSafeHtml(html: string): boolean {
  for (const pattern of DANGEROUS_HTML_PATTERNS) {
    if (pattern.test(html)) return false;
  }
  return true;
}

/**
 * Rules that ADD a missing alt. For these we must never overwrite an alt the
 * site owner has since added on the same image. "Bad alt" rules (e.g. F30)
 * intentionally replace an existing alt and are deliberately excluded.
 */
// scanner#277 (user decision 01.10.2026, way 1): F39 is NOT additive — it
// REPLACES a misleading alt with alt="" on an image that by definition already
// has one, so this guard refused 100 % of its patches. The generator now keeps
// the dangerous case (the image is a control's only name) out instead.
const ADDITIVE_ALT_RULES = new Set(['F65', 'F38']);

/**
 * What a `compound` may carry (scanner#61) — mirrors COMPOUND_PART_TYPES,
 * COMPOUND_MIN_PARTS and COMPOUND_MAX_PARTS in fixer-server/src/util/sanitize.ts,
 * which refuses to store anything else. Defense in depth, and the reason a
 * part can never be `injectStyleRule` (selector-scoped, applied before the
 * element loop in apply()), `replaceTag` (swaps the element, so later parts
 * would patch a detached node) or another `compound`.
 */
const COMPOUND_PART_TYPES = new Set<PatchAction['type']>([
  'replaceTextContent', 'setStyle', 'setAttribute', 'removeAttribute', 'addClass',
]);
const COMPOUND_MIN_PARTS = 2;
const COMPOUND_MAX_PARTS = 4;

/**
 * Normalize an image URL to origin+pathname so a relative src stored at scan
 * time and the live attribute/currentSrc compare equal. Mirrors the server's
 * computeUrlHash normalization (query/hash are not significant).
 */
function normalizeImgSrc(src: string): string {
  try {
    const base = typeof document !== 'undefined' ? document.baseURI : 'http://localhost';
    const u = new URL(src, base);
    return u.origin + u.pathname;
  } catch {
    return src.split(/[?#]/)[0];
  }
}

/**
 * `data-helpbob-patched` holds a space-separated list of patch IDs — one
 * element can carry several patches. Markers are appended/removed per ID
 * so no patch ever clobbers another patch's marker (F5); a lost marker
 * would let the MutationWatcher re-apply the same patch (duplicate
 * description spans, stacked rollback chains).
 */
export function readPatchMarkers(el: Element): string[] {
  const raw = el.getAttribute('data-helpbob-patched');
  return raw ? raw.split(/\s+/).filter(Boolean) : [];
}

function appendPatchMarker(el: Element, patchId: string): void {
  const ids = readPatchMarkers(el);
  if (!ids.includes(patchId)) ids.push(patchId);
  el.setAttribute('data-helpbob-patched', ids.join(' '));
}

function removePatchMarker(el: Element, patchId: string): void {
  const ids = readPatchMarkers(el).filter((id) => id !== patchId);
  if (ids.length === 0) el.removeAttribute('data-helpbob-patched');
  else el.setAttribute('data-helpbob-patched', ids.join(' '));
}

/**
 * Validate a selector destined for a <style> block and CSS-escape the
 * contents of its attribute string literals (F6). Selectors come from page
 * content (`a[href="…"]`), so a crafted attribute value must not be able to
 * escape the declaration block. Structural characters outside a quoted
 * attribute value could close the block (`}`), open one (`{`), start an
 * at-rule (`@`) or — in serialized HTML — terminate the style element
 * (`<`); such selectors are rejected (returns null). Inside quoted strings
 * the same characters (plus commas, which injectStyleRule splits on) are
 * re-emitted as CSS hex escapes; attribute matching compares decoded
 * values, so the escaped selector matches exactly the same elements.
 * Mirrors the server-side check in fixer-server/src/util/sanitize.ts.
 */
/** Characters that must never reach a <style> block unescaped. */
const STRUCTURAL_CHARS = '{}@<>;,';

const hexEscape = (ch: string): string => '\\' + ch.charCodeAt(0).toString(16) + ' ';

/** Re-emit an already-escaped character, hex-encoding the structural ones. */
const reescape = (ch: string): string =>
  STRUCTURAL_CHARS.includes(ch) ? hexEscape(ch) : '\\' + ch;

export function escapeSelector(selector: string): string | null {
  let out = '';
  let brackets = 0;
  let quote: string | null = null;
  for (let i = 0; i < selector.length; i++) {
    const ch = selector[i];
    if (quote !== null) {
      if (ch === '\\') {
        if (i + 1 >= selector.length) return null; // dangling escape
        // A CSS escape carries its character through verbatim — re-emit the
        // dangerous ones as hex so `\<` can't smuggle a literal `</style`
        // into the block (same character, no structural meaning).
        out += reescape(selector[i + 1]);
        i++;
      } else if (ch === quote) {
        quote = null;
        out += ch;
      } else if (ch === '\n' || ch === '\r' || ch === '\f') {
        return null; // CSS "bad string"
      } else if (STRUCTURAL_CHARS.includes(ch)) {
        out += hexEscape(ch);
      } else {
        out += ch;
      }
      continue;
    }
    if (ch === '"' || ch === "'") {
      if (brackets === 0) return null; // strings only occur inside [attr="…"]
      quote = ch;
      out += ch;
    } else if (ch === '[') {
      brackets++;
      out += ch;
    } else if (ch === ']') {
      if (brackets === 0) return null;
      brackets--;
      out += ch;
    } else if (ch === '{' || ch === '}' || ch === '@' || ch === ';' || ch === '<') {
      return null;
    } else if (ch === '/' && selector[i + 1] === '*') {
      return null; // comment could hide any of the above from later checks
    } else if (ch === '\\') {
      if (i + 1 >= selector.length) return null;
      out += reescape(selector[i + 1]); // escaped identifier char (e.g. #a\.b)
      i++;
    } else {
      out += ch;
    }
  }
  return quote === null && brackets === 0 ? out : null;
}

interface AppliedPatch {
  patch: PublicPatch;
  elements: Element[];
  rollback: () => void;
}

/**
 * F110 on the document root (scanner#354): every sticky bar is its own finding
 * and its own patch, and two bars at the same edge both set
 * `scroll-padding-<edge>` on `html`. A plain setStyle let the LAST applied
 * value win — a 60px header applied after a 110px one shrank the band again.
 * Each such patch therefore registers what it needs, and the root carries the
 * LARGEST of them (and never less than the page's own inline px value); an
 * undo withdraws only its own share, in any order, and the last one out hands
 * the declaration back exactly. Keyed on the element, not on an applier, so a
 * review preview and the live patches share one ledger.
 */
const ROOT_SCROLL_PADDING = /^scroll-padding-(top|bottom)$/;
const PX_VALUE = /^(\d+(?:\.\d+)?)px$/;

interface RootPaddingLedger {
  original: string;
  priority: string;
  shares: Map<symbol, number>;
  /** What the ledger last wrote — a different value means the page rewrote the declaration. */
  written: string;
}

const rootPaddingLedgers = new WeakMap<Element, Map<string, RootPaddingLedger>>();

function isRootScrollPadding(
  el: HTMLElement,
  action: Extract<PatchAction, { type: 'setStyle' }>,
): boolean {
  return el === el.ownerDocument.documentElement
    && ROOT_SCROLL_PADDING.test(action.property)
    && PX_VALUE.test(action.value);
}

function applyRootScrollPadding(
  el: HTMLElement,
  action: Extract<PatchAction, { type: 'setStyle' }>,
): () => void {
  let byProperty = rootPaddingLedgers.get(el);
  if (!byProperty) rootPaddingLedgers.set(el, (byProperty = new Map()));
  let ledger = byProperty.get(action.property);
  // A ledger whose value is no longer on the element is stale: the page (or a
  // framework re-render) replaced the style since. Its shares are gone with
  // it; start over from what is there now. Their undos find no share of
  // theirs in the new ledger and do nothing.
  if (!ledger || el.style.getPropertyValue(action.property) !== ledger.written) {
    ledger = {
      original: el.style.getPropertyValue(action.property),
      priority: el.style.getPropertyPriority(action.property),
      shares: new Map(),
      written: '',
    };
    byProperty.set(action.property, ledger);
  }
  const own = Symbol(action.property);
  ledger.shares.set(own, Number(PX_VALUE.exec(action.value)![1]));

  const settle = (l: RootPaddingLedger) => {
    const floor = PX_VALUE.exec(l.original);
    const max = Math.max(floor ? Number(floor[1]) : 0, ...l.shares.values());
    if (floor && max === Number(floor[1])) el.style.setProperty(action.property, l.original, l.priority);
    else el.style.setProperty(action.property, `${max}px`);
    l.written = el.style.getPropertyValue(action.property);
  };
  settle(ledger);

  return () => {
    const l = byProperty!.get(action.property);
    if (!l || !l.shares.delete(own)) return;
    if (l.shares.size > 0) { settle(l); return; }
    byProperty!.delete(action.property);
    if (l.original) el.style.setProperty(action.property, l.original, l.priority);
    else el.style.removeProperty(action.property);
  };
}

/**
 * The result of one action: how to undo it, and WHICH element is in the DOM
 * afterwards. The second half exists because `replaceTag` swaps the node —
 * marking the element we started from left the marker on a node that is no
 * longer in the tree, so the patch counted as applied but was not provable,
 * and the MutationWatcher re-applied it to the unmarked replacement (#226).
 */
interface AppliedAction {
  undo: () => void;
  element: Element;
}

export class PatchApplier {
  private applied: Map<string, AppliedPatch> = new Map();
  /** Paket D: targets resolved before a batch ran (pinTargets). */
  private pinned: Map<string, Element | null> = new Map();

  /**
   * Apply a single patch to matching DOM elements.
   * Returns the number of elements affected.
   *
   * `preview: true` skips the status==='active' gate — used by review mode
   * where the reviewer is explicitly evaluating a pending patch. The gate
   * exists to keep visitor-facing code from applying not-yet-approved
   * mutations; in review mode we know we're not a regular visitor (signed
   * token in the URL), so it's safe to bypass.
   */
  apply(patch: PublicPatch, opts: { preview?: boolean } = {}): number {
    if (!opts.preview && patch.status !== 'active') return 0;
    if (this.applied.has(patch.id)) return 0;

    const elements = this.candidates(patch)
      .filter((el) => !this.shouldSkipElement(el, patch));
    if (elements.length === 0) return 0;

    // injectStyleRule is selector-scoped, not element-scoped: one <style>
    // element covers all current AND future matches (incl. pseudo-classes
    // like :focus that inline styles can't express).
    if (patch.action.type === 'injectStyleRule') {
      const styleEl = this.injectStyleRule(patch.selector, patch.action);
      if (!styleEl) return 0;
      this.applied.set(patch.id, {
        patch,
        elements,
        rollback: () => styleEl.remove(),
      });
      return elements.length;
    }

    const rollbackActions: (() => void)[] = [];
    // scanner#368: only elements the action actually changed count. A refused
    // action (unsafe HTML, existing role/aria, class already there) used to
    // count its element anyway — analytics booked 'applied' and the browser
    // verifier judged a fix no visitor received.
    const changed: Element[] = [];

    for (const el of elements) {
      try {
        const applied = this.applyAction(el, patch.action);
        if (applied) {
          changed.push(applied.element);
          // Mark the element that is IN THE TREE after the action, not the one
          // we started from: `replaceTag` swaps `el` out, so marking `el` left
          // the marker on a detached node and the MutationWatcher re-applied
          // the same patch to the (unmarked) replacement (#226).
          const marked = applied.element;
          appendPatchMarker(marked, patch.id);
          rollbackActions.push(() => {
            applied.undo();
            removePatchMarker(marked, patch.id);
          });
        }
      } catch (err) {
        // Never let a patch error propagate to the customer's code
        // nosemgrep: javascript.lang.security.audit.unsafe-formatstring.unsafe-formatstring
        console.warn('[HelpBob] Patch failed:', patch.id, el, err);
      }
    }

    // Nothing changed → not applied: no entry, so a later apply() may retry
    // (the DOM may have changed by then).
    if (changed.length === 0) return 0;

    this.applied.set(patch.id, {
      patch,
      elements: changed,
      rollback: () => rollbackActions.forEach(fn => fn()),
    });

    return changed.length;
  }

  /**
   * Apply a patch to a single element (used by MutationWatcher for dynamic content).
   * Returns true if the patch was successfully applied.
   */
  applyToElement(el: Element, patch: PublicPatch): boolean {
    if (patch.status !== 'active') return false;
    // Paket D: a patch with a target note means ONE of its selector's matches —
    // only that one, checked against the live page (index, count, fingerprint).
    if (patch.target && this.resolveLive(patch) !== el) return false;
    if (!patch.target && patch.action.type === 'renameId') return false;
    // Own idempotency check (#226). `apply()` is guarded by
    // `this.applied.has(patch.id)`; this entry point had NO guard of its own —
    // the marker check lived solely in the caller (MutationWatcher.applyIfNew),
    // so any other caller could stack the same patch on the same element.
    // Exact token match, never substring (see readPatchMarkers).
    if (readPatchMarkers(el).includes(patch.id)) return false;
    if (this.shouldSkipElement(el, patch)) return false;

    // The injected rule already covers dynamically added elements via its
    // CSS selector — inject once, then just track the new element.
    if (patch.action.type === 'injectStyleRule') {
      const existing = this.applied.get(patch.id);
      if (existing) {
        if (!existing.elements.includes(el)) existing.elements.push(el);
        return true;
      }
      const styleEl = this.injectStyleRule(patch.selector, patch.action);
      if (!styleEl) return false;
      this.applied.set(patch.id, {
        patch,
        elements: [el],
        rollback: () => styleEl.remove(),
      });
      return true;
    }

    try {
      const applied = this.applyAction(el, patch.action);
      if (applied) {
        // The element that is in the tree AFTER the action — `replaceTag`
        // hands back the replacement, every other action hands back `el` (#226).
        const marked = applied.element;
        const undo = applied.undo;
        // Track for rollback — append to existing entry or create one
        const existing = this.applied.get(patch.id);
        if (existing) {
          existing.elements.push(marked);
          const prevRollback = existing.rollback;
          existing.rollback = () => {
            prevRollback();
            undo();
            removePatchMarker(marked, patch.id);
          };
        } else {
          this.applied.set(patch.id, {
            patch,
            elements: [marked],
            rollback: () => {
              undo();
              removePatchMarker(marked, patch.id);
            },
          });
        }

        // Mark element — append patch ID to support multiple patches per element
        appendPatchMarker(marked, patch.id);
        return true;
      }
    } catch (err) {
      console.warn('[HelpBob] Patch failed:', patch.id, el, err);
    }
    return false;
  }

  /**
   * Paket D — resolve every targeted patch of a batch BEFORE any of them runs.
   *
   * The note's index counts the selector's matches on the SCANNED page. A
   * patch of the same batch can change that list — the F77 fix renames one of
   * two `#x`, after which `#x` matches one element and every other note on
   * `#x` (n = 2) would no longer resolve. Pinned up front, all notes are read
   * against the page as it was loaded; `apply()` then uses the pinned element
   * (if it is still in the document) and drops the pin.
   */
  pinTargets(patches: readonly PublicPatch[]): void {
    for (const patch of patches) {
      if (patch.target && !this.applied.has(patch.id)) this.pinned.set(patch.id, this.resolveLive(patch));
    }
  }

  /** The elements a patch may touch: every match, or only its resolved target. */
  private candidates(patch: PublicPatch): Element[] {
    if (!patch.target) {
      // renameId only ever ships with a target (sanitize.ts); without one it
      // would rename every match — refuse rather than guess.
      if (patch.action.type === 'renameId') return [];
      return querySelectorAllDeep(document, patch.selector);
    }
    let el: Element | null;
    if (this.pinned.has(patch.id)) {
      el = this.pinned.get(patch.id) ?? null;
      this.pinned.delete(patch.id);
      if (el && !el.isConnected) el = this.resolveLive(patch);
    } else {
      el = this.resolveLive(patch);
    }
    return el ? [el] : [];
  }

  /** Light-DOM matches, exactly what the scanner counted; null = skip silently. */
  private resolveLive(patch: PublicPatch): Element | null {
    if (!patch.target) return null;
    try {
      return resolveTarget(document.querySelectorAll(patch.selector), patch.target);
    } catch {
      return null;
    }
  }

  /**
   * Remove a previously applied patch, restoring original state.
   */
  rollback(patchId: string): boolean {
    const applied = this.applied.get(patchId);
    if (!applied) return false;

    applied.rollback();
    this.applied.delete(patchId);
    return true;
  }

  /**
   * Remove all applied patches.
   */
  rollbackAll(): void {
    // Newest first (scanner#61): two patches may change the same thing. Undoing
    // in apply order left the FIRST patch's value behind (each undo restores
    // what it saw when it was applied); only the reverse order ends at the
    // original state. F110's root scroll-padding no longer depends on it — its
    // ledger undoes in any order (applyRootScrollPadding, scanner#354).
    for (const id of Array.from(this.applied.keys()).reverse()) {
      this.rollback(id);
    }
  }

  /**
   * Get IDs of all currently applied patches.
   */
  getAppliedIds(): string[] {
    return Array.from(this.applied.keys());
  }

  /**
   * Apply a single action to an element and return a rollback function.
   */
  /**
   * Create the <style> element for an injectStyleRule patch. Defense in
   * depth on top of the server-side whitelist: the selector is validated
   * and its attribute strings CSS-escaped (F6, see escapeSelector) —
   * returns null (patch not applied) when the selector can't be made safe;
   * structural characters that could escape the declaration block are
   * stripped, and the pseudo part must look like a pseudo-class/-element.
   */
  private injectStyleRule(
    selector: string,
    action: Extract<PatchAction, { type: 'injectStyleRule' }>,
  ): HTMLStyleElement | null {
    const safeSelector = escapeSelector(selector);
    if (safeSelector === null) {
      console.warn('[HelpBob] Blocked unsafe selector in injectStyleRule patch');
      return null;
    }
    const declarations = action.declarations.replace(/[{}<>@\\]/g, '');
    const pseudo = action.pseudo && /^::?[a-zA-Z-]+$/.test(action.pseudo) ? action.pseudo : '';
    // Apply the pseudo to every selector in a comma-separated list.
    // (Commas inside attribute strings were hex-escaped above, so this
    // split can no longer cut a quoted attribute value in half.)
    const scopedSelector = safeSelector
      .split(',')
      .map((s) => `${s.trim()}${pseudo}`)
      .join(', ');

    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-helpbob-style', 'true');
    styleEl.textContent = `${scopedSelector} { ${declarations} }`;
    document.head.appendChild(styleEl);
    return styleEl;
  }

  /**
   * Decide whether a matched element must be SKIPPED for this patch, even
   * though its selector matched. Two guards:
   *  1. Identity: the patch carries an expected image src and the live element
   *     is now a different image (swapped at the same selector) → never apply a
   *     stale alt to the new image.
   *  2. Alt-clobber: an "add missing alt" patch must not overwrite an alt the
   *     site owner has since added on the same image.
   * Legacy patches (no expectedImg) and non-image patches are unaffected.
   */
  private shouldSkipElement(el: Element, patch: PublicPatch): boolean {
    return this.identityMismatch(el, patch) || this.altClobber(el, patch);
  }

  private identityMismatch(el: Element, patch: PublicPatch): boolean {
    const want = patch.expectedImg;
    if (!want || !want.src) return false; // legacy / non-image → never blocks
    const live = el.getAttribute('src') || (el as HTMLImageElement).currentSrc || '';
    if (!live) return false; // not yet loaded / no src → don't block
    return normalizeImgSrc(live) !== normalizeImgSrc(want.src);
  }

  private altClobber(el: Element, patch: PublicPatch): boolean {
    if (patch.action.type !== 'setAttribute' || patch.action.attribute !== 'alt') return false;
    if (!ADDITIVE_ALT_RULES.has(patch.ruleId)) return false;
    const existing = el.getAttribute('alt');
    return !!existing && existing.trim() !== '';
  }

  private applyAction(el: Element, action: PatchAction): AppliedAction | null {
    // Which node carries the patch marker afterwards? Actions that mutate in
    // place answer `el`; actions that SWAP the node (replaceTag) must report
    // their replacement, or the marker ends up on a detached node and the
    // MutationWatcher applies the same patch again (#226). Any future
    // replacing/moving action reports itself through `onReplace` — one line,
    // and the marker follows automatically.
    //
    // `wrapElement` deliberately does NOT report: it inserts a wrapper AROUND
    // `el`, so `el` itself stays in the tree and keeps carrying the marker.
    let inTree: Element = el;
    const undo = this.runAction(el, action, (replacement) => { inTree = replacement; });
    return undo ? { undo, element: inTree } : null;
  }

  private runAction(
    el: Element,
    action: PatchAction,
    onReplace: (replacement: Element) => void,
  ): (() => void) | null {
    switch (action.type) {
      case 'setAttribute': {
        // F40/F58 (21.10.2026): `content` only on a real <meta http-equiv=
        // "refresh"> and only as "<seconds>[;url=<http(s)|relative>]" — the
        // server checks the selector, only the page knows the element.
        if (String(action.attribute).toLowerCase() === META_REFRESH_CONTENT_ATTRIBUTE
          && (!isMetaRefreshElement(el) || metaRefreshContentError(action.value) !== null)) {
          return null;
        }
        const prev = el.getAttribute(action.attribute);
        el.setAttribute(action.attribute, action.value);
        return () => {
          if (prev === null) el.removeAttribute(action.attribute);
          else el.setAttribute(action.attribute, prev);
        };
      }

      case 'removeAttribute': {
        const prev = el.getAttribute(action.attribute);
        if (prev === null) return null;
        el.removeAttribute(action.attribute);
        return () => el.setAttribute(action.attribute, prev);
      }

      case 'addAriaLabel': {
        // SAFETY: Never overwrite an existing aria-label
        if (el.hasAttribute('aria-label')) return null;
        el.setAttribute('aria-label', action.value);
        return () => el.removeAttribute('aria-label');
      }

      case 'addAriaDescribedBy': {
        // Meaning (scanner#368): `descriptionText` IS the description a screen
        // reader reads out; `value` is only the id SUGGESTED for the
        // developer's source fix and is deliberately NOT used here — an id the
        // LLM invented may already exist on this page, or more than once. So
        // the description element gets its own collision-free id. The server
        // refuses an empty text (util/sanitize.ts); this guard covers stored
        // patches from before that check: no text, nothing to describe.
        if (typeof action.descriptionText !== 'string' || action.descriptionText.trim() === '') return null;
        const descId = `helpbob-desc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const descEl = document.createElement('span');
        descEl.id = descId;
        descEl.textContent = action.descriptionText;
        descEl.setAttribute('style', 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;');
        descEl.setAttribute('data-helpbob-generated', 'true');

        el.parentNode?.insertBefore(descEl, el.nextSibling);

        const prev = el.getAttribute('aria-describedby');
        const newVal = prev ? `${prev} ${descId}` : descId;
        el.setAttribute('aria-describedby', newVal);

        return () => {
          descEl.remove();
          if (prev === null) el.removeAttribute('aria-describedby');
          else el.setAttribute('aria-describedby', prev);
        };
      }

      case 'setRole': {
        // SAFETY: Never overwrite an existing explicit role
        if (el.hasAttribute('role')) return null;
        el.setAttribute('role', action.role);
        return () => el.removeAttribute('role');
      }

      case 'addClass': {
        if (el.classList.contains(action.className)) return null;
        el.classList.add(action.className);
        return () => el.classList.remove(action.className);
      }

      case 'setStyle': {
        const htmlEl = el as HTMLElement;
        if (isRootScrollPadding(htmlEl, action)) return applyRootScrollPadding(htmlEl, action);
        // Paket FX (F94): the rem bounds of the fixed clamp() were computed
        // for `1rem` = the visitor's default size. A page that resized its
        // root font (html{font-size:62.5%}) would get other bounds than the
        // customer approved — then nothing is applied.
        if (String(action.property).toLowerCase() === 'font-size' && isViewportFontClamp(action.value)
          && !remIsUserDefault()) {
          return null;
        }
        const prev = htmlEl.style.getPropertyValue(action.property);
        // Restored with its priority: setProperty drops an inline !important,
        // and an undo must hand the element back exactly (#61, F110 on html).
        const prevPriority = htmlEl.style.getPropertyPriority(action.property);
        htmlEl.style.setProperty(action.property, action.value);
        return () => {
          if (prev) htmlEl.style.setProperty(action.property, prev, prevPriority);
          else htmlEl.style.removeProperty(action.property);
        };
      }

      case 'wrapElement': {
        const wrapper = document.createElement(action.wrapperTag);
        if (action.wrapperAttributes) {
          for (const [k, v] of Object.entries(action.wrapperAttributes)) {
            wrapper.setAttribute(k, v);
          }
        }
        wrapper.setAttribute('data-helpbob-generated', 'true');
        el.parentNode?.insertBefore(wrapper, el);
        wrapper.appendChild(el);
        return () => {
          wrapper.parentNode?.insertBefore(el, wrapper);
          wrapper.remove();
        };
      }

      case 'insertAdjacentHTML': {
        // Paket AX (F25, missing <title>): the one insert that creates a page
        // title. Only into the real <head>, only while the page has no HTML
        // <title> of its own (one added since the scan wins), and built with
        // textContent — the string is never parsed into the page. DOMParser
        // only decodes the entities of the plain text (RCDATA: no elements).
        if (isPageTitleInsert(action.html)) {
          if (el !== document.head) return null;
          const hasTitle = Array.from(document.getElementsByTagName('title'))
            .some((t) => t.namespaceURI === 'http://www.w3.org/1999/xhtml');
          if (hasTitle) return null;
          const text = new DOMParser().parseFromString(action.html, 'text/html').title;
          if (!text) return null;
          const title = document.createElement('title');
          title.textContent = text;
          title.setAttribute('data-helpbob-generated', 'true');
          el.insertBefore(title, action.position === 'afterbegin' ? el.firstChild : null);
          return () => { title.remove(); };
        }
        // Paket FF (bypass-blocks): the one insert that creates a link — a
        // skip link to an id that exists in THIS document, built with
        // textContent (never parsed into the page), hidden until focused via
        // one shared rule. A second skip link to the same target is not added.
        const skip = skipLinkInsertParts(action.html);
        if (skip) {
          if (action.position !== 'beforebegin' && action.position !== 'afterbegin') return null;
          if (!document.getElementById(skip.targetId)) return null;
          const href = '#' + skip.targetId;
          const existing = Array.from(document.querySelectorAll('a.' + SKIP_LINK_CLASS))
            .some((a) => a.getAttribute('href') === href);
          if (existing) return null;
          let style = document.getElementById('hb-skip-link-style');
          const ownStyle = !style;
          if (!style) {
            style = document.createElement('style');
            style.id = 'hb-skip-link-style';
            style.setAttribute('data-helpbob-generated', 'true');
            style.textContent = SKIP_LINK_STYLE;
            (document.head || document.documentElement).appendChild(style);
          }
          const link = document.createElement('a');
          link.setAttribute('href', href);
          link.className = SKIP_LINK_CLASS;
          link.textContent = skip.text;
          link.setAttribute('data-helpbob-generated', 'true');
          el.insertAdjacentElement(action.position, link);
          const styleEl = style;
          return () => {
            link.remove();
            if (ownStyle && !document.querySelector('a.' + SKIP_LINK_CLASS)) styleEl.remove();
          };
        }
        // Paket FX (F16): the one insert that creates a button — a pause
        // control right before an element that really moves (a <marquee>, or
        // a running CSS animation on the element itself). Built here with
        // textContent; the behaviour is this bundle's, never the page's HTML.
        const pause = motionPauseInsertParts(action.html);
        if (pause) return insertMotionPause(el, action.position, pause);
        // Defense-in-depth: validate HTML client-side before DOM insertion
        // Primary sanitization happens server-side in sanitize.ts (tag/attribute whitelist)
        if (!isSafeHtml(action.html)) {
          console.warn('[HelpBob] Blocked unsafe HTML in insertAdjacentHTML patch');
          return null;
        }
        // Create a marker to track what was inserted
        const marker = document.createComment(`helpbob-insert-${Date.now()}`);
        // Use DOMParser instead of innerHTML — DOMParser does not execute scripts
        // and is the recommended safe alternative for parsing HTML strings
        const parsed = new DOMParser().parseFromString(action.html, 'text/html');
        // Defense in depth (security review 13.10.2026): the parsed tree must
        // pass the SAME tag/attribute allowlist the server checks — the string
        // patterns above cannot see `<div/x-init=…>` or `<span title="a>"
        // x-init=…>`, which run code on Alpine/Vue sites. Anything the parser
        // hoisted into <head> (style, meta, …) is refused too.
        const treeError = parsed.head.childNodes.length > 0
          ? 'HTML outside the body'
          : disallowedHtmlNode(parsed.body);
        if (treeError) {
          console.warn('[HelpBob] Blocked unsafe HTML in insertAdjacentHTML patch:', treeError);
          return null;
        }
        const insertedNodes: Node[] = Array.from(parsed.body.childNodes).map(n =>
          document.adoptNode(n.cloneNode(true)),
        );

        // scanner#354 (F81): several findings can insert the SAME markup at the
        // same place — every radio of a group targets the one legend. Such
        // markup carries data-helpbob-key="<key>"; when every key is already
        // in the document the effect is there, and this patch changes nothing.
        // Mirrored in fixer-server/src/scheduler/patch-verifier.ts and
        // test-harness/src/patch-applicator.ts + public/harness-panel.js.
        const dedupeKeys = insertedNodes
          .filter((n): n is Element => n instanceof Element && n.hasAttribute('data-helpbob-key'))
          .map((n) => n.getAttribute('data-helpbob-key') as string);
        if (dedupeKeys.length > 0 && dedupeKeys.every((key) => hasDedupeKey(key))) {
          return () => { /* the first patch with this key owns the node */ };
        }

        // Mark all inserted nodes
        for (const node of insertedNodes) {
          if (node instanceof Element) {
            node.setAttribute('data-helpbob-generated', 'true');
          }
        }

        if (action.position === 'beforebegin' || action.position === 'afterend') {
          const ref = action.position === 'beforebegin' ? el : el.nextSibling;
          const parent = el.parentNode;
          if (!parent) return null;
          parent.insertBefore(marker, ref);
          for (const node of insertedNodes) {
            parent.insertBefore(node, ref);
          }
        } else {
          const ref = action.position === 'afterbegin' ? el.firstChild : null;
          el.insertBefore(marker, ref);
          for (const node of insertedNodes) {
            el.insertBefore(node, ref);
          }
        }

        return () => {
          marker.remove();
          for (const node of insertedNodes) {
            node.parentNode?.removeChild(node);
          }
        };
      }

      case 'replaceTag': {
        const parent = el.parentNode;
        if (!parent) return null;

        const newEl = document.createElement(action.newTag);
        // Copy child nodes
        while (el.firstChild) {
          newEl.appendChild(el.firstChild);
        }
        // Copy attributes (optionally filtered)
        for (const attr of Array.from(el.attributes)) {
          if (action.removeAttributes?.includes(attr.name)) continue;
          if (action.preserveAttributes && !action.preserveAttributes.includes(attr.name)) continue;
          // If no preserveAttributes specified, copy all (minus removeAttributes)
          if (!action.preserveAttributes) {
            newEl.setAttribute(attr.name, attr.value);
          } else {
            newEl.setAttribute(attr.name, attr.value);
          }
        }
        // Add new attributes (e.g. scope="col" for th). Never an event
        // handler, inline CSS or an iframe document (#351 H1): the server
        // checks addAttributes since 28.09.2026, a patch stored before still
        // gets here.
        if (action.addAttributes) {
          for (const [k, v] of Object.entries(action.addAttributes)) {
            if (isForbiddenAddedAttribute(k)) continue;
            newEl.setAttribute(k, v);
          }
        }
        newEl.setAttribute('data-helpbob-generated', 'true');
        parent.replaceChild(newEl, el);
        // The marker belongs on the node that is now in the tree (#226).
        onReplace(newEl);

        // Store original element for rollback
        const origEl = el;
        return () => {
          // Move children back
          while (newEl.firstChild) {
            origEl.appendChild(newEl.firstChild);
          }
          newEl.removeAttribute('data-helpbob-generated');
          parent.replaceChild(origEl, newEl);
        };
      }

      case 'replaceTextContent': {
        // Never on the document scaffold: setting textContent deletes EVERY
        // child element — on <head> the <meta>/<link>/<script>, on <body> the
        // whole page (scanner#340). The server refuses to store such a patch;
        // this guard holds regardless of the selector's spelling and for any
        // patch stored before that check existed.
        if (el === document.documentElement || el === document.head || el === document.body) {
          return null;
        }
        const prevText = el.textContent;
        el.textContent = action.text;
        return () => {
          el.textContent = prevText;
        };
      }

      case 'renameId': {
        // Paket D (F77): the element must still carry the duplicated id, and
        // the new one must be free — a page that since gained `to` keeps its
        // own element, and we change nothing.
        const { from, to } = action;
        if (typeof from !== 'string' || typeof to !== 'string' || !from || !to || from === to) return null;
        if (/\s/.test(to) || el.getAttribute('id') !== from || document.getElementById(to)) return null;
        // The one reference that provably means THIS element: a label that
        // wraps it and names the old id. Every other reference stays and now
        // resolves to the element that keeps the id.
        const label = el.parentElement ? el.parentElement.closest('label') : null;
        const ownLabel = label && label.getAttribute('for') === from ? label : null;
        el.setAttribute('id', to);
        if (ownLabel) ownLabel.setAttribute('for', to);
        return () => {
          if (el.getAttribute('id') === to) el.setAttribute('id', from);
          if (ownLabel && ownLabel.getAttribute('for') === to) ownLabel.setAttribute('for', from);
        };
      }

      case 'compound': {
        // scanner#61: several parts, ONE fix. Applied in order through the
        // same per-action code as single patches; all or nothing — when a part
        // refuses (returns null) or throws, the parts already applied are undone
        // at once and the element is left as it was (no half-applied fix). The
        // compound's undo reverts every part, in reverse order.
        const parts = Array.isArray(action.actions) ? action.actions : [];
        if (parts.length < COMPOUND_MIN_PARTS || parts.length > COMPOUND_MAX_PARTS) return null;
        const undos: Array<() => void> = [];
        // setStyle's undo drops the declaration but leaves an empty `style=""`
        // on an element that had no style attribute — kept as it is for single
        // patches; a compound promises the element exactly as it was.
        const hadStyle = el.hasAttribute('style');
        // Same for addClass: removing the class leaves `class=""` (#354).
        const hadClass = el.hasAttribute('class');
        const undoAll = () => {
          for (let i = undos.length - 1; i >= 0; i--) undos[i]();
          if (!hadStyle && el.getAttribute('style') === '') el.removeAttribute('style');
          if (!hadClass && el.getAttribute('class') === '') el.removeAttribute('class');
        };
        for (const part of parts) {
          let undo: (() => void) | null = null;
          if (part && COMPOUND_PART_TYPES.has(part.type)) {
            try {
              undo = this.runAction(el, part, onReplace);
            } catch (err) {
              console.warn('[HelpBob] Compound part failed:', part.type, err);
              undo = null;
            }
          }
          if (!undo) {
            undoAll();
            return null;
          }
          undos.push(undo);
        }
        return undoAll;
      }

      default:
        return null;
    }
  }
}

/**
 * Paket FX (F94): is `1rem` on this page the visitor's default font size?
 * `font-size: medium` resolves to that default whatever the page's root
 * size is; equal → the page left the root alone (or set 100 %).
 */
function remIsUserDefault(): boolean {
  try {
    const root = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const probe = document.createElement('span');
    probe.style.cssText = 'position:absolute;visibility:hidden;font-size:medium';
    (document.body || document.documentElement).appendChild(probe);
    const medium = parseFloat(getComputedStyle(probe).fontSize);
    probe.remove();
    return root > 0 && medium > 0 && Math.abs(root - medium) < 0.5;
  } catch {
    return false;
  }
}

/** Does `el` move in a way the pause control can stop? */
function motionOf(el: Element): 'marquee' | 'css' | null {
  if (el.localName === 'marquee') {
    const m = el as Element & { stop?: () => void; start?: () => void };
    return typeof m.stop === 'function' && typeof m.start === 'function' ? 'marquee' : null;
  }
  try {
    const cs = getComputedStyle(el);
    const name = cs.animationName;
    const duration = cs.animationDuration;
    if (!name || name === 'none' || !duration || /^0s(,\s*0s)*$/.test(duration)) return null;
    return 'css';
  } catch {
    return null;
  }
}

/**
 * Paket FX (F16): insert the pause control before `el` and wire it. Fail
 * closed — nothing is inserted when the element is in a shadow root or an
 * iframe's document (the snippet only owns this document's light DOM), in a
 * control or a hidden/inert area, already has a HelpBob control, or does not
 * move in a way animation-play-state / marquee.stop() can halt (a ticker
 * moved by a script would keep running behind a "Pause" button).
 */
function insertMotionPause(
  el: Element,
  position: InsertPosition,
  labels: { lang: string; pause: string; resume: string },
): (() => void) | null {
  if (position !== 'beforebegin') return null;
  if (el.ownerDocument !== document || el.getRootNode() !== document) return null;
  const parent = el.parentElement;
  if (!parent || el === document.body || el === document.documentElement) return null;
  if (parent.closest(MOTION_PAUSE_BLOCKED_ANCESTORS)) return null;
  if (el.hasAttribute(MOTION_CONTROLLED_ATTRIBUTE)) return null;
  const motion = motionOf(el);
  if (!motion) return null;

  let style = document.getElementById('hb-motion-pause-style');
  const ownStyle = !style;
  if (!style) {
    style = document.createElement('style');
    style.id = 'hb-motion-pause-style';
    style.setAttribute('data-helpbob-generated', 'true');
    style.textContent = MOTION_PAUSE_STYLE;
    (document.head || document.documentElement).appendChild(style);
  }
  const button = document.createElement('button');
  button.type = 'button';
  button.className = MOTION_PAUSE_CLASS;
  button.setAttribute('lang', labels.lang);
  button.setAttribute('data-helpbob-generated', 'true');
  button.textContent = labels.pause;
  let paused = false;
  const setPaused = (next: boolean): void => {
    paused = next;
    if (motion === 'marquee') {
      const m = el as Element & { stop: () => void; start: () => void };
      if (paused) m.stop(); else m.start();
    } else if (paused) {
      el.setAttribute(MOTION_PAUSED_ATTRIBUTE, '');
    } else {
      el.removeAttribute(MOTION_PAUSED_ATTRIBUTE);
    }
    button.textContent = paused ? labels.resume : labels.pause;
  };
  // A native <button>: Enter and Space click it, it is in the tab order.
  button.addEventListener('click', () => setPaused(!paused));
  el.setAttribute(MOTION_CONTROLLED_ATTRIBUTE, '');
  el.insertAdjacentElement('beforebegin', button);
  const styleEl = style;
  return () => {
    if (paused) setPaused(false);
    button.remove();
    el.removeAttribute(MOTION_CONTROLLED_ATTRIBUTE);
    if (ownStyle && !document.querySelector('button.' + MOTION_PAUSE_CLASS)) styleEl.remove();
  };
}
